
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { File, Trash, Upload } from "lucide-react";

interface FileUploadProps {
  id: string;
  disabled?: boolean;
  onUpload: (url: string) => void;
  buttonText?: string;
}

export function FileUpload({ id, disabled = false, onUpload, buttonText = "Upload" }: FileUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string>("");
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };
  
  const handleUpload = () => {
    if (!file) return;
    
    // In a real implementation, this would upload to a storage service
    // For now, we'll simulate an upload with a local URL
    const mockUrl = URL.createObjectURL(file);
    setUploadedUrl(mockUrl);
    onUpload(mockUrl);
    
    toast({
      title: "File uploaded",
      description: `${file.name} has been uploaded successfully.`
    });
    
    // Reset the file input
    setFile(null);
  };
  
  const handleRemove = () => {
    setUploadedUrl("");
    onUpload("");
    toast({
      title: "File removed",
      description: "The uploaded file has been removed."
    });
  };
  
  return (
    <div className="space-y-2">
      {!uploadedUrl ? (
        <div className="flex items-center gap-2">
          <Input
            id={id}
            type="file"
            onChange={handleFileChange}
            disabled={disabled}
            className="flex-1"
          />
          <Button 
            onClick={handleUpload} 
            disabled={!file || disabled}
            size="sm"
          >
            <Upload className="h-4 w-4 mr-2" /> {buttonText}
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between p-2 border rounded-md">
          <div className="flex items-center">
            <File className="h-5 w-5 mr-2 text-blue-500" />
            <span className="text-sm truncate max-w-[200px]">
              {file?.name || "Uploaded file"}
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleRemove}
            disabled={disabled}
          >
            <Trash className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      )}
    </div>
  );
}
